<template>
	<!-- teleported to body so position:fixed isn't clipped to .layout-content's nav-bar-reduced box on mobile -->
	<Teleport to="body">
		<div v-if="isOpen" class="modal-overlay" @click="closeModal">
			<div class="modal-content" @click.stop>
				<div class="modal-header">
					<h3>{{ modalTitle }}</h3>
					<button type="button" class="close-btn" :title="$t('callToActions.close')" @click="closeModal">
						<AppIcon :name="IconName.CloseSmall" :size="16" :stroke-width="1.3" />
					</button>
				</div>

				<div class="modal-body" :class="{ loading: isLoading }">
					<div v-if="isLoading" class="loader-overlay">
						<div class="loader"></div>
						<p>{{ $t("project.loading") }}</p>
					</div>

					<!-- Project type -->
					<div class="form-field">
						<label>{{ $t("project.type-label") }}</label>
						<input v-if="isUpdateMode" type="text" :value="projectTypeLabel" readonly />
						<ArkListboxDropdown
							v-else
							v-model="localProjectType"
							:options="projectTypeOptions"
							:multiple="false"
							:clearable="false"
							:searchable="false"
						/>
					</div>

					<!-- Workspace owner -->
					<div v-if="isWorkspaceMode" class="form-field">
						<label>{{ $t("project.workspace-owner") }}</label>
						<ArkListboxDropdown
							v-model="localWorkspaceOwnerId"
							:options="workspaceOwnerOptions"
							:multiple="false"
							:clearable="true"
							:searchable="true"
							:placeholder="$t('project.workspace-owner')"
							:searchNormalizer="normalizeLabel"
						/>
						<p v-if="ownerHasWorkspace" class="field-hint">{{ $t("project.workspace-exists-hint") }}</p>
					</div>

					<!-- Contract -->
					<div v-if="!isWorkspaceMode" class="form-field">
						<div class="field-label-row">
							<label>{{ $t("project.contract") }}</label>
							<Button
								v-if="localContractId"
								target="_blank"
								:href="`${HUBSPOT_URL}${localContractId}`"
								variant="ghost"
								size="s"
								class="open-contract-link"
							>
								{{ $t("project.open-contract") }}
								<AppIcon :name="IconName.ExternalLink" :size="12" :stroke-width="2" />
							</Button>
						</div>
						<ArkListboxDropdown
							v-model="localContractId"
							:options="contractOptions"
							:multiple="false"
							:clearable="true"
							:searchable="true"
							:placeholder="$t('project.contract')"
							:searchNormalizer="normalizeLabel"
						/>
					</div>

					<!-- Name -->
					<div v-if="!isWorkspaceMode" class="form-field">
						<label>{{ $t("project.project-input") }}</label>
						<input type="text" v-model.trim="localProjectName" :placeholder="$t('project.project-input')" />
					</div>

					<!-- Broker -->
					<div v-if="!isWorkspaceMode" class="form-field">
						<label>{{ $t("project.brokers") }}</label>
						<ArkListboxDropdown
							v-model="localSelectedBrokers"
							:options="brokersOptions"
							:multiple="true"
							:clearable="true"
							:searchable="true"
							:placeholder="$t('project.brokers')"
							:searchNormalizer="normalizeLabel"
						/>
					</div>

					<!-- Prospector -->
					<div v-if="!isWorkspaceMode" class="form-field">
						<label>{{ $t("project.prospectors") }}</label>
						<ArkListboxDropdown
							v-model="localSelectedProspectorIds"
							:options="prospectorsOptions"
							:multiple="true"
							:clearable="true"
							:searchable="true"
							:placeholder="$t('project.prospectors')"
							:searchNormalizer="normalizeLabel"
						/>
					</div>

					<!-- Expiration date -->
					<div v-if="!isWorkspaceMode" class="form-field">
						<label>{{ $t("project.expiry-date") }}</label>
						<SingleDatePicker v-model="localExpiryDate" :locale="datePickerLocale" />
					</div>
					<!-- Send Invites Section -->
					<div v-if="isUpdateMode" class="send-invites-section">
						<h4>{{ $t("project.send-invites") }}</h4>
						<!-- Existing clients display -->
						<div v-if="existingClients.length > 0" class="existing-clients-info">
							<p class="info-text">
								<span class="label">{{ $t("project.already-invited-clients") }}</span>
								<span class="client-names">
									{{ existingClientsNames }}
								</span>
							</p>
						</div>
						<!-- Clients -->
						<div class="form-field">
							<ArkListboxDropdown
								v-model="localSelectedClientsIds"
								:options="clientsOptions"
								:multiple="true"
								:clearable="true"
								:searchable="true"
								:placeholder="$t('project.clients')"
								:searchNormalizer="normalizeLabel"
							/>
						</div>
					</div>
				</div>

				<div class="modal-footer">
					<Button
						v-if="isUpdateMode"
						variant="primary"
						:size="isMobile ? 's' : 'm'"
						:disabled="!canSendInvites || isSendingInvites"
						@click="handleSendInvites"
					>
						<span v-if="isSendingInvites" class="loader"></span>
						<span v-else>{{ $t("project.send-invites") }}</span>
					</Button>
					<Button
						variant="primary"
						:size="isMobile ? 's' : 'm'"
						:disabled="!canSaveProject || isSavingProject"
						@click="handleSaveProject"
					>
						<span v-if="isSavingProject" class="loader"></span>
						<span v-else>{{ modalTitle }}</span>
					</Button>
				</div>
			</div>
		</div>
	</Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, inject, toRaw } from "vue"
import { useI18n } from "vue-i18n"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { useMediaQuery } from "@vueuse/core"
import ArkListboxDropdown from "@/components/ui/ArkListboxDropdown.vue"
import SingleDatePicker from "@/components/ui/SingleDatePicker.vue"
import Button from "@/components/ui/Button.vue"
import { UseRootStore, Permission } from "@/model/RootStore"
import {
	getCCAContracts,
	getUsersByRole,
	getUsersByPermission,
	inviteContactsToProject,
	getCCAClients,
	getUserHasWorkspace,
} from "@/utils/ApiClient"
import { ProjectStoreKey } from "@/stores/projectStore.ts"
import { useToastStore } from "@/stores/toastStore.ts"
import { formatDateToISO } from "@/utils/date"
import { config, Feature } from "@/AppConfig.ts"
import type { Project } from "@/model/DataModel"

const HUBSPOT_URL = import.meta.env.VITE_HUBSPOT_DEAL_ENDPOINT
const store = UseRootStore()
const projectStore = inject(ProjectStoreKey)!()
const { createProject, updateProject, createWorkspace } = projectStore
const toastStore = useToastStore()
const isMobile = useMediaQuery("(max-width: 768px)")
const { t } = useI18n()

const datePickerLocale = computed(() => (store.currentLocale === "fr" ? "fr-FR" : "en-US"))

const isSavingProject = ref(false)
const isSendingInvites = ref(false)

interface CCAContract {
	hubspotRecordId: string
	contractName: string
}

interface UserOption {
	name: string
	id: string
}

interface Client {
	id?: string | null
	firstName?: string | null
	lastName?: string | null
	email?: string | null
}

interface Props {
	isOpen: boolean
	currentProject?: {
		id: string
		name: string
		type?: string
		ccaContractHubspotId: string
		expiryDate?: string | null
		brokers?: Array<{ id: string }>
		prospectors?: Array<{ id: string }>
		clients?: Array<Client>
	} | null
}

interface Emits {
	(e: "close"): void
	(e: "created", project: Project): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// Modal data state
const ccaContracts = ref<CCAContract[]>([])
const brokers = ref<UserOption[]>([])
const prospectors = ref<UserOption[]>([])
const clients = ref<Client[]>([])
const isLoading = ref(false)

// Local state for form fields
const localProjectType = ref<"CCA" | "WORKSPACE">("CCA")
const localWorkspaceOwnerId = ref<string | null>(null)
const ownerHasWorkspace = ref(false)
const workspaceOwners = ref<{ id: string; name: string }[]>([])
const localContractId = ref<string | null>(null)
const localProjectName = ref<string>("")
const localSelectedBrokers = ref<string[] | null>(null)
const localSelectedProspectorIds = ref<string[] | null>(null)
const localExpiryDate = ref<Date | null>(null)
const localSelectedClientsIds = ref<string[] | null>(null)

const isUpdateMode = computed(() => props.currentProject !== null && props.currentProject !== undefined)
const isWorkspaceMode = computed(() => localProjectType.value === "WORKSPACE")
const projectTypeOptions = computed(() => [
	{ value: "CCA", label: t("project.type-cca") },
	{ value: "WORKSPACE", label: t("project.type-workspace") },
])
const projectTypeLabel = computed(() => {
	if (props.currentProject?.type === "WORKSPACE") return t("project.type-workspace")
	if (props.currentProject?.type === "CCV") return "CCV"
	return t("project.type-cca")
})

// Existing clients that are already been invited to this project
const existingClients = ref<Client[]>([])

// Computed options for dropdowns
const contractOptions = computed(() =>
	ccaContracts.value.map(contract => ({
		value: contract.hubspotRecordId,
		label: contract.contractName,
	}))
)

const brokersOptions = computed(() =>
	brokers.value.map(broker => ({
		value: broker.id,
		label: broker.name,
	}))
)

const prospectorsOptions = computed(() =>
	prospectors.value.map(prospector => ({
		value: prospector.id,
		label: prospector.name,
	}))
)

const workspaceOwnerOptions = computed(() => workspaceOwners.value.map(user => ({ value: user.id, label: user.name })))

const clientsOptions = computed(() => {
	const allClients = [...existingClients.value, ...clients.value]

	const clientsMap = new Map<string, Client>()
	allClients.forEach((contact: Client) => {
		const email = contact.email?.toLowerCase() || ""
		if (email && !clientsMap.has(email)) {
			clientsMap.set(email, contact)
		} else if (!email) {
			const id = contact.id || ""
			if (id && !clientsMap.has(id)) {
				clientsMap.set(id, contact)
			}
		}
	})

	const options = Array.from(clientsMap.values()).map((contact: Client, index: number) => {
		const uniqueValue = contact.id || contact.email || `contact-${index}`
		const firstName = contact.firstName || ""
		const lastName = contact.lastName || ""
		const fullName = `${firstName} ${lastName}`.trim()

		// Check if this client is already been invited to this project
		const isExisting = existingClients.value.some(
			ec => (ec.id && contact.id && ec.id === contact.id) || ec.email?.toLowerCase() === contact.email?.toLowerCase()
		)

		const baseLabel = fullName || contact.email || contact.id || `Contact ${index + 1}`
		const label = isExisting ? `${baseLabel} (Already invited)` : baseLabel

		return {
			value: uniqueValue,
			label,
		}
	})

	return options
})

// Function to load all data needed for the modal
async function loadModalData() {
	if (!store.currentUser?.has(Permission.ProjectWrite)) {
		return
	}

	try {
		const token = store.userApiToken
		const [contractsResult, brokerUsers, prospectorUsers] = await Promise.all([
			getCCAContracts(token),
			getUsersByRole(token, "broker"),
			getUsersByRole(token, "prospector"),
		])

		brokers.value = brokerUsers.map(user => ({
			name: user.name,
			id: user.id.toString(),
		}))
		prospectors.value = prospectorUsers.map(user => ({
			name: user.name,
			id: user.id.toString(),
		}))
		ccaContracts.value = contractsResult.map(contract => ({
			hubspotRecordId: contract.hubspotRecordId,
			contractName: contract.contractName,
		}))
	} catch (err) {
		console.error("Failed to load modal data:", err)
	}
}

// Function to load clients for a contract
async function loadClientsForContract(contractId: string) {
	try {
		const token = store.userApiToken
		const result = await getCCAClients(token, contractId)
		clients.value = result
	} catch (err) {
		console.error("Failed to load clients for contract:", err)
		clients.value = []
	}
}

// Extract initial values from currentProject
function extractInitialValues() {
	if (props.currentProject) {
		const existingClientsList =
			props.currentProject.clients?.map(client => ({
				id: client.id,
				firstName: client.firstName || null,
				lastName: client.lastName || null,
				email: client.email || null,
			})) || []
		existingClients.value = existingClientsList
		return {
			contractId: props.currentProject.ccaContractHubspotId || null,
			projectName: props.currentProject.name || "",
			selectedBrokers: props.currentProject.brokers?.map(u => u.id.toString()) || null,
			selectedProspectorIds: props.currentProject.prospectors?.map(u => u.id.toString()) || null,
			expiryDate: props.currentProject.expiryDate ? new Date(props.currentProject.expiryDate) : null,
		}
	}
	existingClients.value = []
	return {
		contractId: null,
		projectName: "",
		selectedBrokers: null,
		selectedProspectorIds: null,
		expiryDate: null,
	}
}

// Watch for prop changes when modal opens
watch(
	() => props.isOpen,
	async open => {
		if (open) {
			// Extract initial values from currentProject
			const initial = extractInitialValues()
			localProjectType.value = "CCA"
			localWorkspaceOwnerId.value = null
			ownerHasWorkspace.value = false
			localContractId.value = initial.contractId
			localProjectName.value = initial.projectName
			localSelectedBrokers.value = initial.selectedBrokers
			localSelectedProspectorIds.value = initial.selectedProspectorIds
			localExpiryDate.value = initial.expiryDate
			localSelectedClientsIds.value = null

			// Start loading
			isLoading.value = true

			try {
				// Load modal data if not already loaded
				const needsModalData =
					ccaContracts.value.length === 0 || brokers.value.length === 0 || prospectors.value.length === 0
				const modalDataPromise = needsModalData ? loadModalData() : Promise.resolve()

				// Load clients only if updating an existing project and a contract is already selected
				const clientsPromise =
					isUpdateMode.value && initial.contractId ? loadClientsForContract(initial.contractId) : Promise.resolve()

				// Wait for all data to load
				await Promise.all([modalDataPromise, clientsPromise])
			} catch (error) {
				console.error("Error loading modal data:", error)
			} finally {
				isLoading.value = false
			}
		}
	}
)

// Watch for contract selection changes to load clients and auto-fill project name
watch(
	() => localContractId.value,
	async newContractId => {
		if (props.isOpen && newContractId) {
			// Auto-fill project name from contract if empty
			if (!localProjectName.value || localProjectName.value.trim() === "") {
				const selectedContract = ccaContracts.value.find(contract => contract.hubspotRecordId === newContractId)
				if (selectedContract) {
					localProjectName.value = selectedContract.contractName
				}
			}

			// Only load clients if updating an existing project
			if (isUpdateMode.value) {
				// Show loader only if modal is fully loaded (not in initial loading state)
				const wasLoading = isLoading.value
				if (!wasLoading) {
					isLoading.value = true
				}
				try {
					await loadClientsForContract(newContractId)
				} catch (error) {
					console.error("Error loading clients:", error)
				} finally {
					// Only set to false if we set it to true in this watch
					if (!wasLoading) {
						isLoading.value = false
					}
				}
			} else {
				// Clear clients when creating a new project
				clients.value = []
			}
		} else {
			clients.value = []
		}
	}
)

// load the eligible Workspace owners (users who can write targets) the first time the mode is used
watch(
	() => isWorkspaceMode.value,
	async workspace => {
		if (!workspace || workspaceOwners.value.length > 0) return
		try {
			workspaceOwners.value = await getUsersByPermission(store.userApiToken, Permission.OpportunityTargetWrite)
		} catch (err) {
			console.error("Failed to load workspace owners:", err)
		}
	}
)

// Flag when the selected Workspace owner already has one (creation would be a no-op).
watch(
	() => localWorkspaceOwnerId.value,
	async ownerId => {
		if (!ownerId) {
			ownerHasWorkspace.value = false
			return
		}
		try {
			ownerHasWorkspace.value = await getUserHasWorkspace(store.userApiToken, Number(ownerId))
		} catch (err) {
			console.error("Failed to check owner workspace:", err)
			ownerHasWorkspace.value = false
		}
	}
)

// Watch for changes to currentProject when modal is open
watch(
	() => props.currentProject,
	project => {
		if (props.isOpen && project) {
			const initial = extractInitialValues()
			localContractId.value = initial.contractId
			localProjectName.value = initial.projectName
			localSelectedBrokers.value = initial.selectedBrokers
			localSelectedProspectorIds.value = initial.selectedProspectorIds
			localExpiryDate.value = initial.expiryDate
		}
	}
)

const canSaveProject = computed(() => {
	if (isWorkspaceMode.value) {
		return localWorkspaceOwnerId.value !== null && !ownerHasWorkspace.value
	}
	return (
		localProjectName.value.trim() !== "" &&
		localContractId.value !== null &&
		localSelectedBrokers.value !== null &&
		localSelectedBrokers.value.length > 0 &&
		localExpiryDate.value !== null
	)
})

const modalTitle = computed(() => {
	if (isUpdateMode.value) return t("project.updateProject")
	return isWorkspaceMode.value ? t("project.createWorkspace") : t("project.createProject")
})

const existingClientsNames = computed(() => {
	if (existingClients.value.length === 0) {
		return ""
	}
	return existingClients.value
		.map(client => {
			const firstName = client.firstName || ""
			const lastName = client.lastName || ""
			const fullName = `${firstName} ${lastName}`.trim()
			return fullName || client.email || client.id || "Unknown"
		})
		.join(", ")
})

const canSendInvites = computed(() => {
	return isUpdateMode.value && localSelectedClientsIds.value !== null && localSelectedClientsIds.value.length > 0
})

function normalizeLabel(name: string) {
	return name
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
}

function closeModal() {
	emit("close")
}

async function handleSaveProject() {
	if (!canSaveProject.value) {
		return
	}

	if (isWorkspaceMode.value) {
		if (!localWorkspaceOwnerId.value) {
			toastStore.error("project.workspaceOwnerRequired")
			return
		}
		isSavingProject.value = true
		try {
			const createdWorkspace = await createWorkspace(Number(localWorkspaceOwnerId.value))
			toastStore.success("project.workspaceCreateSuccess")
			if (createdWorkspace) {
				emit("created", createdWorkspace)
			}
			emit("close")
		} catch (error) {
			console.error("Error creating workspace:", error)
			toastStore.error("project.saveError")
		} finally {
			isSavingProject.value = false
		}
		return
	}

	// Validation
	if (!localProjectName.value || localProjectName.value.trim() === "") {
		toastStore.error("project.nameRequired")
		return
	}

	if (!localContractId.value && config.ui.has(Feature.SelectProjectsFromHubSpot)) {
		toastStore.error("project.contractRequired")
		return
	}

	if (!localSelectedBrokers.value || localSelectedBrokers.value.length === 0) {
		toastStore.error("project.brokersRequired")
		return
	}

	if (!localExpiryDate.value) {
		toastStore.error("project.expiryDateRequired")
		return
	}

	// Convert to numbers
	const selectedBrokerIds = (localSelectedBrokers.value as string[]).map((id: string) => Number(id))
	const selectedProspectorIdsArray = (localSelectedProspectorIds.value || []).map((id: string) => Number(id))
	const existingClientIds = existingClients.value.filter(client => client.id != null).map(client => Number(client.id))

	const contractId = localContractId.value || ""
	const expiry = formatDateToISO(localExpiryDate.value)
	const project = props.currentProject ? toRaw(props.currentProject) : null

	isSavingProject.value = true
	try {
		if (project != null && project.id != null) {
			await updateProject(
				project.id,
				localProjectName.value,
				contractId,
				selectedBrokerIds,
				expiry,
				selectedProspectorIdsArray,
				existingClientIds
			)
			toastStore.success("project.updateSuccess")
		} else {
			const createdProject = await createProject(
				localProjectName.value,
				contractId,
				selectedBrokerIds,
				expiry,
				selectedProspectorIdsArray
			)
			toastStore.success("project.createSuccess")
			// switches the modal into update mode in place, so invites can be sent without closing and reopening it
			if (createdProject) {
				emit("created", createdProject)
			}
		}
	} catch (error) {
		console.error("Error saving project:", error)
		toastStore.error("project.saveError")
	} finally {
		isSavingProject.value = false
	}
}

async function handleSendInvites() {
	if (!canSendInvites.value || !localSelectedClientsIds.value || !props.currentProject) {
		return
	}

	// Find contacts that match the selected IDs from both HubSpot clients and existing clients
	const allAvailableClients = [...clients.value, ...existingClients.value]
	const selectedContacts = allAvailableClients.filter((contact: Client) => {
		const contactId = contact.id || contact.email || ""
		return localSelectedClientsIds.value?.includes(contactId)
	})

	// Map to invitees format required by the mutation
	const invitees = selectedContacts.map(contact => ({
		email: contact.email || "",
		firstName: contact.firstName || undefined,
		lastName: contact.lastName || undefined,
	}))

	// Filter out any contacts without email
	const validInvitees = invitees.filter(invitee => invitee.email)

	if (validInvitees.length === 0) {
		console.error("No valid clients with email addresses selected")
		toastStore.error("project.clientEmailsRequired")
		return
	}

	isSendingInvites.value = true
	try {
		const token = store.userApiToken
		const success = await inviteContactsToProject(token, props.currentProject.id, validInvitees)

		if (success) {
			const inviteCount = validInvitees.length
			if (inviteCount === 1) {
				toastStore.success("project.inviteSent")
			} else {
				toastStore.success("project.invitesSent", { params: { count: inviteCount } })
			}
			localSelectedClientsIds.value = null

			if (props.currentProject) {
				await projectStore.fetchProjectMetadata(props.currentProject.id, { includePeople: true })
			}
		} else {
			console.error("Failed to send invites")
			toastStore.error("project.invitesError")
		}
	} catch (error) {
		console.error("Error sending invites:", error)
		toastStore.error("project.invitesError")
	} finally {
		isSendingInvites.value = false
	}
}
</script>

<style scoped>
.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 1100;
	backdrop-filter: blur(4px);
}

.modal-content {
	background: var(--neutral-0);
	border-radius: var(--border-radius-lg);
	width: 90%;
	max-width: 600px;
	max-height: 90vh;
	display: flex;
	flex-direction: column;
	box-shadow: var(--shadow-xl);
	overflow: hidden;
}

.modal-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: var(--spacing-md) var(--spacing-xl);
	border-bottom: var(--border-width-thin) solid var(--neutral-400);

	h3 {
		margin: 0;
		font-family: var(--font-family-primary);
		font-size: var(--font-size-medium);
		font-weight: var(--font-weight-bold);
		color: var(--neutral-900);
	}

	.close-btn {
		background: none;
		border: none;
		cursor: pointer;
		color: var(--neutral-600);
		width: var(--size-icon-xl);
		height: var(--size-icon-xl);
		display: flex;
		align-items: center;
		justify-content: center;
		transition: color 0.2s ease;

		&:hover {
			color: var(--neutral-900);
		}
	}
}

.modal-body {
	flex: 1;
	overflow-y: auto;
	padding: var(--spacing-xl);
	display: flex;
	flex-direction: column;
	gap: var(--spacing-lg);
	position: relative;

	&.loading {
		pointer-events: none;
		opacity: 0.6;
	}

	.loader-overlay {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		background-color: rgba(255, 255, 255, 0.9);
		z-index: 10;
		gap: var(--spacing-md);

		p {
			font-family: var(--font-family-primary);
			font-size: var(--font-size-base);
			color: var(--neutral-900);
			margin: 0;
		}
	}

	.loader {
		width: var(--size-icon-lg);
		height: var(--size-icon-lg);
		border: var(--border-width-base) solid var(--primary-green);
		border-bottom-color: transparent;
		border-radius: var(--border-radius-full);
		display: inline-block;
		animation: rotation 1s linear infinite;
	}
}

@keyframes rotation {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

.form-field {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-xs);

	label {
		font-family: var(--font-family-primary);
		font-size: var(--font-size-base);
		font-weight: var(--font-weight-medium);
		color: var(--neutral-900);
	}

	.field-hint {
		margin: 0;
		font-family: var(--font-family-primary);
		font-size: var(--font-size-small);
		color: var(--neutral-700);
	}

	:deep([data-scope="popover"][data-part="positioner"]) {
		width: 100%;
	}

	:deep([data-scope="popover"][data-part="content"]) {
		width: 100%;
	}

	input {
		width: 100%;
		height: var(--spacing-xl);
		padding: var(--spacing-xxs) var(--spacing-xs) var(--spacing-xxs) var(--space-10);
		font-family: var(--font-family-primary);
		font-size: var(--font-size-regular);
		border: var(--border-width-thin) solid var(--neutral-400);
		border-radius: var(--border-radius-sm);

		&:focus {
			outline: none;
			border-color: var(--primary-green);
		}
	}

	.field-label-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--spacing-sm);

		.open-contract-link {
			gap: var(--space-4);
		}
	}
}

.send-invites-section {
	margin-top: var(--spacing-md);
	padding-top: var(--spacing-md);
	border-top: var(--border-width-thin) solid var(--neutral-400);

	h4 {
		margin: 0 0 var(--spacing-md) 0;
		font-family: var(--font-family-primary);
		font-size: var(--font-size-base);
		font-weight: var(--font-weight-bold);
		color: var(--neutral-900);
	}

	.existing-clients-info {
		margin-bottom: var(--spacing-md);

		.info-text {
			margin: 0;
			font-family: var(--font-family-primary);
			font-size: var(--font-size-small);
			color: var(--neutral-900);
			display: flex;
			flex-direction: column;
			gap: var(--spacing-xxs);

			.label {
				font-weight: var(--font-weight-medium);
				color: var(--primary-green);
			}

			.client-names {
				color: var(--neutral-600);
				font-style: italic;
			}
		}
	}
}

.modal-footer {
	display: flex;
	justify-content: flex-end;
	gap: var(--spacing-sm);
	padding: var(--spacing-lg) var(--spacing-xl);
	border-top: var(--border-width-thin) solid var(--neutral-400);
	background-color: var(--neutral-0);

	.loader {
		width: var(--size-icon-sm);
		height: var(--size-icon-sm);
		border: var(--border-width-base) solid var(--neutral-0);
		border-bottom-color: transparent;
		border-radius: var(--border-radius-full);
		display: inline-block;
		animation: rotation 1s linear infinite;
	}
}

@media (max-width: 768px) {
	.modal-overlay {
		align-items: flex-end;
	}

	.modal-content {
		width: 100%;
		max-width: 100%;
		max-height: 92vh;
		border-radius: var(--border-radius-lg) var(--border-radius-lg) 0 0;
	}

	.modal-header,
	.modal-body,
	.modal-footer {
		padding-left: var(--spacing-md);
		padding-right: var(--spacing-md);
	}

	.modal-footer {
		justify-content: center;
	}
}
</style>
